import { useEffect, useMemo, useState } from "react";
import { api, downloadAuthed } from "@/lib/api";
import { useSiteContent } from "@/context/SiteContentContext";
import {
    TrendingUp,
    Calendar,
    Package,
    AlertTriangle,
    BarChart3,
    Receipt,
    ShoppingBag,
    Download,
} from "lucide-react";

const RANGES = [
    { id: 7, label: "7d" },
    { id: 30, label: "30d" },
    { id: 60, label: "60d" },
    { id: 90, label: "90d" },
];

function fmtEur(n) {
    return `€${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDay(iso) {
    // "2026-04-19" → "Apr 19"
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

function fmtMonth(iso) {
    // "2026-04" → "Apr"
    const d = new Date(iso + "-01T00:00:00Z");
    return d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" });
}

/** Pure-SVG bar chart. No external deps. */
function BarChart({ data, valueKey, labelFmt, height = 180, color = "hsl(var(--primary))", testid }) {
    const max = Math.max(1, ...data.map((d) => d[valueKey] || 0));
    const barWidth = 100 / data.length;
    return (
        <svg viewBox={`0 0 100 ${height + 24}`} preserveAspectRatio="none" className="w-full" style={{ height: `${height + 24}px` }} data-testid={testid}>
            {/* Gridlines */}
            {[0.25, 0.5, 0.75, 1].map((p) => (
                <line key={p} x1="0" y1={height - height * p} x2="100" y2={height - height * p}
                    stroke="#e2e8f0" strokeWidth="0.2" strokeDasharray="0.5 0.5" />
            ))}
            {data.map((d, i) => {
                const v = d[valueKey] || 0;
                const h = (v / max) * height;
                const x = i * barWidth + barWidth * 0.15;
                const w = barWidth * 0.7;
                return (
                    <g key={i}>
                        <title>{`${labelFmt(d)} — ${valueKey === "revenue" ? fmtEur(v) : v}`}</title>
                        <rect x={x} y={height - h} width={w} height={h} fill={color} />
                    </g>
                );
            })}
            {/* X axis tick labels — show every Nth */}
            {data.map((d, i) => {
                const step = Math.max(1, Math.ceil(data.length / 8));
                if (i % step !== 0 && i !== data.length - 1) return null;
                return (
                    <text key={`t-${i}`} x={i * barWidth + barWidth / 2} y={height + 18}
                        fontSize="3" fill="#64748b" textAnchor="middle" fontFamily="ui-monospace, monospace">
                        {labelFmt(d)}
                    </text>
                );
            })}
        </svg>
    );
}

function Stat({ icon: Icon, label, value, sub, accent }) {
    return (
        <div className={`bg-white border p-4 ${accent ? "border-amber-400" : "border-slate-200"}`}>
            <div className="flex items-center gap-1.5 label-kicker text-slate-500 mb-1">
                <Icon className="w-3.5 h-3.5" /> {label}
            </div>
            <div className={`font-display font-black text-3xl ${accent ? "text-amber-700" : ""}`}>{value}</div>
            {sub && <div className="text-[11px] font-mono text-slate-500 mt-1">{sub}</div>}
        </div>
    );
}

export default function AnalyticsTab() {
    const { content } = useSiteContent();
    const rate = content?.currency_lek_per_eur || 100;
    const [range, setRange] = useState(30);
    const [data, setData] = useState(null);
    const [topData, setTopData] = useState(null);
    const [alerts, setAlerts] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            api.get("/admin/sales/analytics", { params: { days: range } }),
            api.get("/admin/sales/stats"),
            api.get("/admin/stock-alerts", { params: { threshold: 5 } }),
        ])
            .then(([a, s, al]) => {
                setData(a.data);
                setTopData(s.data);
                setAlerts(al.data);
            })
            .catch((e) => console.error("analytics load", e))
            .finally(() => setLoading(false));
    }, [range]);

    const avgDaily = useMemo(() => {
        if (!data) return 0;
        return data.period_revenue / Math.max(1, data.days);
    }, [data]);

    if (loading && !data) {
        return <div data-testid="analytics-loading" className="font-mono text-sm text-slate-500 p-8">Loading analytics…</div>;
    }
    if (!data) {
        return <div className="font-mono text-sm text-red-600 p-8">Could not load analytics.</div>;
    }

    const maxCatRevenue = Math.max(1, ...(data.by_category || []).map((c) => c.revenue));

    return (
        <div data-testid="admin-analytics-tab" className="space-y-6">
            {/* Range picker */}
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="label-kicker text-slate-500">Period</div>
                    <div className="font-display font-black text-xl uppercase">Sales analytics</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1 border border-slate-200 p-1">
                        {RANGES.map((r) => (
                            <button
                                key={r.id}
                                onClick={() => setRange(r.id)}
                                className={`px-3 py-1.5 text-xs font-mono font-bold uppercase ${
                                    range === r.id
                                        ? "bg-slate-900 text-white"
                                        : "text-slate-600 hover:bg-slate-100"
                                }`}
                                data-testid={`analytics-range-${r.id}`}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>
                    <button
                        type="button"
                        onClick={() => downloadAuthed(
                            "/admin/sales/analytics/export",
                            `turbo-cimi-analytics-${range}d.xlsx`,
                            { days: range }
                        ).catch((e) => alert(e.message))}
                        className="btn-outline"
                        data-testid="analytics-export-xlsx"
                        title="Export multi-sheet XLSX with all analytics + stock alerts"
                    >
                        <Download className="w-4 h-4" /> Export XLSX
                    </button>
                </div>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat
                    icon={TrendingUp}
                    label={`Revenue (last ${data.days}d)`}
                    value={fmtEur(data.period_revenue)}
                    sub={`${Math.round(data.period_revenue * rate).toLocaleString()} Lek`}
                />
                <Stat
                    icon={Receipt}
                    label={`Sales (last ${data.days}d)`}
                    value={data.period_count}
                    sub={`Avg ${fmtEur(avgDaily)}/day`}
                />
                <Stat
                    icon={Package}
                    label="Total stock qty"
                    value={data.stock_health.total_qty}
                    sub={`${data.stock_health.in_stock} SKUs in stock`}
                />
                <Stat
                    icon={AlertTriangle}
                    label="Low / Out of stock"
                    value={`${data.stock_health.low_stock} / ${data.stock_health.out_of_stock}`}
                    sub="≤5 units · zero units"
                    accent={data.stock_health.low_stock > 0 || data.stock_health.out_of_stock > 0}
                />
            </div>

            {/* Daily revenue chart */}
            <div className="border border-slate-200 bg-white">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-slate-500" />
                    <div className="font-display font-bold uppercase text-sm">Daily revenue · last {data.days} days</div>
                    <div className="ml-auto font-mono text-xs text-slate-500">{fmtEur(data.period_revenue)} total</div>
                </div>
                <div className="p-4">
                    <BarChart
                        data={data.daily}
                        valueKey="revenue"
                        labelFmt={(d) => fmtDay(d.date)}
                        height={140}
                        testid="analytics-daily-chart"
                    />
                </div>
            </div>

            {/* 2-column row: Monthly bars + Category breakdown */}
            <div className="grid lg:grid-cols-2 gap-6">
                <div className="border border-slate-200 bg-white">
                    <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-500" />
                        <div className="font-display font-bold uppercase text-sm">Monthly revenue · last 12 months</div>
                    </div>
                    <div className="p-4">
                        <BarChart
                            data={data.monthly}
                            valueKey="revenue"
                            labelFmt={(d) => fmtMonth(d.month)}
                            height={140}
                            color="#0f172a"
                            testid="analytics-monthly-chart"
                        />
                    </div>
                </div>

                <div className="border border-slate-200 bg-white">
                    <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                        <ShoppingBag className="w-4 h-4 text-slate-500" />
                        <div className="font-display font-bold uppercase text-sm">Revenue by category</div>
                    </div>
                    <div className="p-4">
                        {data.by_category.length === 0 ? (
                            <div className="text-center font-mono text-sm text-slate-400 py-8">
                                No sales recorded yet.
                            </div>
                        ) : (
                            <div className="space-y-3" data-testid="analytics-by-category">
                                {data.by_category.map((c) => (
                                    <div key={c.category_id || c.category_name} className="space-y-1">
                                        <div className="flex justify-between font-mono text-xs">
                                            <span className="font-bold uppercase truncate pr-2">{c.category_name}</span>
                                            <span className="text-slate-700">{fmtEur(c.revenue)} <span className="text-slate-400">· {c.qty} pcs</span></span>
                                        </div>
                                        <div className="h-1.5 bg-slate-100 overflow-hidden">
                                            <div
                                                className="h-full bg-[hsl(var(--primary))]"
                                                style={{ width: `${(c.revenue / maxCatRevenue) * 100}%` }}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Top products + Top customers */}
            <div className="grid lg:grid-cols-2 gap-6">
                <div className="border border-slate-200 bg-white">
                    <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                        <Package className="w-4 h-4 text-slate-500" />
                        <div className="font-display font-bold uppercase text-sm">Top 5 products</div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                                    <th className="px-3 py-2 label-kicker">Part</th>
                                    <th className="px-3 py-2 label-kicker">Name</th>
                                    <th className="px-3 py-2 label-kicker text-right">Qty</th>
                                    <th className="px-3 py-2 label-kicker text-right">Revenue</th>
                                </tr>
                            </thead>
                            <tbody data-testid="analytics-top-products">
                                {(topData?.top_products || []).length === 0 && (
                                    <tr><td colSpan="4" className="px-3 py-6 text-center font-mono text-slate-400">No data.</td></tr>
                                )}
                                {(topData?.top_products || []).map((p) => (
                                    <tr key={p.product_id} className="border-b border-slate-100">
                                        <td className="px-3 py-2 font-mono text-[11px] text-slate-700">{p.part_number || "—"}</td>
                                        <td className="px-3 py-2 truncate max-w-[260px]">{p.name}</td>
                                        <td className="px-3 py-2 text-right font-mono font-bold">{p.qty_sold}</td>
                                        <td className="px-3 py-2 text-right font-mono">{fmtEur(p.revenue)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="border border-slate-200 bg-white">
                    <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                        <Receipt className="w-4 h-4 text-slate-500" />
                        <div className="font-display font-bold uppercase text-sm">Top 5 customers</div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200 text-left">
                                    <th className="px-3 py-2 label-kicker">Customer</th>
                                    <th className="px-3 py-2 label-kicker text-right">Sales</th>
                                    <th className="px-3 py-2 label-kicker text-right">Total spent</th>
                                </tr>
                            </thead>
                            <tbody data-testid="analytics-top-customers">
                                {(topData?.top_customers || []).length === 0 && (
                                    <tr><td colSpan="3" className="px-3 py-6 text-center font-mono text-slate-400">No data.</td></tr>
                                )}
                                {(topData?.top_customers || []).map((c) => (
                                    <tr key={c.id} className="border-b border-slate-100">
                                        <td className="px-3 py-2 font-bold">{c.name}</td>
                                        <td className="px-3 py-2 text-right font-mono">{c.sales_count}</td>
                                        <td className="px-3 py-2 text-right font-mono">{fmtEur(c.total_spent)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Stock Alerts */}
            {alerts && alerts.total_alerts > 0 && (
                <div className="border border-amber-300 bg-amber-50" data-testid="analytics-stock-alerts">
                    <div className="px-4 py-3 border-b border-amber-300 bg-amber-100 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-700" />
                        <div className="font-display font-bold uppercase text-sm text-amber-900">
                            Stock alerts · {alerts.total_alerts} produkte kërkojnë rifurnizim
                        </div>
                        <div className="ml-auto font-mono text-xs text-amber-800">
                            {alerts.out_of_stock.length} pa stok · {alerts.low_stock.length} stok i ulët (≤ {alerts.threshold})
                        </div>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-amber-50 border-b border-amber-200 text-left">
                                    <th className="px-3 py-2 label-kicker">Status</th>
                                    <th className="px-3 py-2 label-kicker">Part</th>
                                    <th className="px-3 py-2 label-kicker">Name</th>
                                    <th className="px-3 py-2 label-kicker text-right">Qty</th>
                                    <th className="px-3 py-2 label-kicker text-right">Price</th>
                                </tr>
                            </thead>
                            <tbody>
                                {alerts.out_of_stock.slice(0, 25).map((p) => (
                                    <tr key={`out-${p.id}`} className="border-b border-amber-100" data-testid={`stock-alert-out-${p.id}`}>
                                        <td className="px-3 py-2"><span className="text-[10px] font-mono font-bold uppercase bg-red-100 text-red-800 px-2 py-0.5">Pa stok</span></td>
                                        <td className="px-3 py-2 font-mono text-[11px]">{p.part_number || "—"}</td>
                                        <td className="px-3 py-2 truncate max-w-[260px]">{p.name}</td>
                                        <td className="px-3 py-2 text-right font-mono font-bold text-red-700">{p.stock_qty}</td>
                                        <td className="px-3 py-2 text-right font-mono">{p.price != null ? fmtEur(p.price) : "—"}</td>
                                    </tr>
                                ))}
                                {alerts.low_stock.slice(0, 25).map((p) => (
                                    <tr key={`low-${p.id}`} className="border-b border-amber-100" data-testid={`stock-alert-low-${p.id}`}>
                                        <td className="px-3 py-2"><span className="text-[10px] font-mono font-bold uppercase bg-amber-200 text-amber-900 px-2 py-0.5">Stok i ulët</span></td>
                                        <td className="px-3 py-2 font-mono text-[11px]">{p.part_number || "—"}</td>
                                        <td className="px-3 py-2 truncate max-w-[260px]">{p.name}</td>
                                        <td className="px-3 py-2 text-right font-mono font-bold text-amber-700">{p.stock_qty}</td>
                                        <td className="px-3 py-2 text-right font-mono">{p.price != null ? fmtEur(p.price) : "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {(alerts.out_of_stock.length + alerts.low_stock.length > 50) && (
                        <div className="px-4 py-2 text-[11px] font-mono text-amber-800 border-t border-amber-200">
                            Showing first 50. Full list available in the XLSX export.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
